/**
 * Landing — the public marketing page.
 *
 * A cinematic, mostly-dark scroll built on three real things rather than
 * mockups: a hand-rolled WebGL panorama (src/lib/panorama.ts) renders every
 * 360° surface, the editor demonstrates itself through an embedded
 * /demo/editor iframe, and the "try it" section embeds an actually published
 * tour. Motion is GSAP + Lenis; the choreography (pinned sequences, scroll
 * snapping, section hand-offs) is desktop-only — below 1000px everything
 * stacks and the secondary WebGL viewers are created and destroyed as they
 * pass the viewport, because phones can't hold that many GL contexts.
 *
 * Styling lives in styles/landing.css, fully scoped under `.lp` so the
 * landing's own token set can't leak into the app's design system. A landing
 * tem um ÚNICO look (escuro): tema claro/escuro é escolha de quem usa o
 * sistema, dentro do app — aqui não há toggle nem paleta clara.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createLensViewer, type LensViewer } from '@/lib/lens-viewer';
import { useTranslation } from 'react-i18next';
import { useJsonLd } from '@/lib/json-ld';
import { SOLUTION_SLUGS, SOLUTION_NAME_KEY } from '@/pages/Solution';
import { Link } from '@tanstack/react-router';
import { appUrl } from '@/lib/host-split';
// Preço da landing sai da MESMA fonte que a cobrança. Enquanto era literal
// no JSX, a vitrine dizia R$ 89 e o cartão era debitado em R$ 89,90.
import { PLANS, formatPriceBRL, AI_COST_CREDITS } from '@my360/shared';

/** GB e gerações saem do plano, não do texto — foi assim que a landing
 *  passou a anunciar 2 GB depois da cota virar 1, e 20 gerações de IA num
 *  plano que entrega 8. Número em string de tradução envelhece calado. */
const planFacts = (slug: 'free' | 'pro' | 'enterprise') => ({
  gb: Math.round(PLANS[slug].limits.maxStorage / 1024 ** 3),
  gens: Math.floor((PLANS[slug].limits.aiCreditsMonthly ?? 0) / AI_COST_CREDITS.generate),
});
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import Lenis from 'lenis';
import { createPanorama, type Panorama } from '@/lib/panorama';
import { Flag } from '@/components/LanguageSelector';
import { BrandLoader } from '@/components/ui/BrandLoader';
import { languages, type LanguageCode } from '@/i18n';
import { useAuthStore } from '@/store/authStore';
import '@/styles/landing.css';

gsap.registerPlugin(ScrollTrigger, SplitText, DrawSVGPlugin);

const DEMO_SHARE_ID = 'k8HXRyc6eU0K8GKG';
const APK_URL = 'https://my360.site/downloads/my360.apk';

/** Generator vocabulary → the editor's real per-type colours. */
const TYPE_META: Record<string, { c: string; ic: string }> = {
  navigation: { c: '#10B981', ic: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M12 2l7 20-7-5-7 5 7-20z"/></svg>' },
  info: { c: '#7C3AED', ic: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M5 5h14M12 5v14" stroke-linecap="round"/></svg>' },
  image: { c: '#3B82F6', ic: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 17l5-5 4 4 3-3 6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>' },
  url: { c: '#F97316', ic: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M10 14a5 5 0 007 0l3-3a5 5 0 00-7-7l-1.5 1.5M14 10a5 5 0 00-7 0l-3 3a5 5 0 007 7l1.5-1.5" stroke-linecap="round"/></svg>' },
  video: { c: '#EF4444', ic: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M10 9l5 3-5 3V9z" fill="currentColor" stroke="none"/></svg>' },
  contact: { c: '#10B981', ic: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6" stroke-linecap="round" stroke-linejoin="round"/></svg>' },
};

interface AiHotspot { type: string; title: string; x: number; y: number }

function LangSwitcher() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = (i18n.language?.startsWith('pt') ? 'pt-BR' : i18n.language?.split('-')[0] || 'pt-BR') as LanguageCode;
  return (
    <div className="lp-lang">
      <button type="button" aria-label={t('landing:a11y.language')} onClick={() => setOpen((v) => !v)} className="lp-icon-btn">
        <Flag code={current} className="h-4 w-6 rounded-sm" />
      </button>
      {open && (
        <>
          <div className="lp-lang-scrim" onClick={() => setOpen(false)} />
          <div className="lp-lang-menu">
            {(Object.keys(languages) as LanguageCode[]).map((code) => (
              <button key={code} type="button" onClick={() => { void i18n.changeLanguage(code); setOpen(false); }}>
                <Flag code={code} className="h-3.5 w-5 rounded-sm" />
                {languages[code].name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Landing() {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const rootRef = useRef<HTMLDivElement>(null);
  const [vsTab, setVsTab] = useState<'matterport' | 'cloudpano' | 'kuula' | 'hugepano'>('matterport');
  /** Flipped the instant the curtains start parting, so the hero's own
   *  entrance plays UNDER them — that overlap is the whole effect. */
  const [revealed, setRevealed] = useState(false);
  const heroTlRef = useRef<gsap.core.Timeline | null>(null);
  /**
   * What the intro's counter actually measures. The hero panorama is the
   * page's heaviest asset by far, so opening the curtains before its
   * texture exists is precisely what made the reveal feel delayed — you
   * were shown an empty sphere and the photo arrived afterwards.
   */
  const [ready, setReady] = useState({ fonts: false, pano: false, spots: false });
  const progress = (Number(ready.fonts) + Number(ready.pano) + Number(ready.spots)) / 3;

  /** Stable callback for the layout effect, which must not re-run when
   *  readiness changes (that would rebuild every WebGL context). */
  const markReadyRef = useRef((k: 'fonts' | 'pano' | 'spots') => {
    setReady((r) => (r[k] ? r : { ...r, [k]: true }));
  });

  useEffect(() => {
    let alive = true;
    const mark = (k: keyof typeof ready) =>
      alive && setReady((r) => (r[k] ? r : { ...r, [k]: true }));
    // Fonts: the wordmark and headline are display faces; revealing before
    // they swap in shows the fallback for a frame.
    if (document.fonts?.ready) void document.fonts.ready.then(() => mark('fonts'));
    else mark('fonts');
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Comparison data. Only what each platform advertises publicly. ──
  const NO = t('landing:compare.no');
  const NA = t('landing:compare.na');
  const USD = t('landing:compare.usd');
  const VS: Record<string, [string, string, boolean, string][]> = {
    matterport: [
      [t('landing:compare.rAi'), t('landing:compare.myAi'), false, NO],
      [t('landing:compare.rAiSpots'), t('landing:compare.myAiSpots'), false, NO],
      [t('landing:compare.rVrMode'), t('landing:compare.myVrMode'), false, NA],
      [t('landing:compare.rBrl'), t('landing:compare.myBrl'), false, USD],
      [t('landing:compare.rUnlimited'), t('landing:compare.myUnlimited'), false, t('landing:compare.tSpaces')],
      [t('landing:compare.rWl'), t('landing:compare.myWl'), false, t('landing:compare.tBrand')],
      [t('landing:compare.rFree'), t('landing:compare.myFree'), false, t('landing:compare.tFreeLimited')],
      [t('landing:compare.rApps'), t('landing:compare.myApps'), false, NA],
      [t('landing:compare.rLeads'), t('landing:compare.myLeads'), true, t('landing:compare.tIntegrations')],
      [t('landing:compare.rVr'), t('landing:compare.myVr'), true, t('landing:compare.tSupported')],
    ],
    cloudpano: [
      [t('landing:compare.rAi'), t('landing:compare.myAi'), false, NO],
      [t('landing:compare.rAiSpots'), t('landing:compare.myAiSpots'), false, NO],
      [t('landing:compare.rVrMode'), t('landing:compare.myVrMode'), false, NA],
      [t('landing:compare.rBrl'), t('landing:compare.myBrl'), false, USD],
      [t('landing:compare.rFree'), t('landing:compare.myFree'), false, t('landing:compare.tTrial')],
      [t('landing:compare.rApps'), t('landing:compare.myApps'), false, NA],
      [t('landing:compare.rMedia'), t('landing:compare.myMedia'), false, t('landing:compare.tPhotoFocus')],
      [t('landing:compare.rWl'), t('landing:compare.myWl'), true, t('landing:compare.tPricier')],
      [t('landing:compare.rLeads'), t('landing:compare.myLeads'), true, t('landing:compare.tSupported')],
      [t('landing:compare.rVr'), t('landing:compare.myVr'), true, t('landing:compare.tSupported')],
    ],
    kuula: [
      [t('landing:compare.rAi'), t('landing:compare.myAi'), false, NO],
      [t('landing:compare.rAiSpots'), t('landing:compare.myAiSpots'), false, NO],
      [t('landing:compare.rVrMode'), t('landing:compare.myVrMode'), false, NA],
      [t('landing:compare.rBrl'), t('landing:compare.myBrl'), false, USD],
      [t('landing:compare.rLeads'), t('landing:compare.myLeads'), false, t('landing:compare.tNotFocus')],
      [t('landing:compare.rApps'), t('landing:compare.myApps'), false, NA],
      [t('landing:compare.rDomain'), t('landing:compare.myWl'), false, t('landing:compare.tHigherPlans')],
      [t('landing:compare.rMedia'), t('landing:compare.myMedia'), true, t('landing:compare.tPartial')],
      [t('landing:compare.rFree'), t('landing:compare.myFree'), true, t('landing:compare.tFreePosts')],
      [t('landing:compare.rVr'), t('landing:compare.myVr'), true, t('landing:compare.tSupported')],
    ],
    hugepano: [
      [t('landing:compare.rAi'), t('landing:compare.myAi'), false, NO],
      [t('landing:compare.rAiSpots'), t('landing:compare.myAiSpots'), false, NA],
      [t('landing:compare.rVrMode'), t('landing:compare.myVrMode'), false, NA],
      [t('landing:compare.rApps'), t('landing:compare.myApps'), false, NA],
      [t('landing:compare.rBrl'), t('landing:compare.myBrl'), false, USD],
      [t('landing:compare.rUnlimited'), t('landing:compare.myUnlimited'), false, t('landing:compare.tPlanLimit')],
      [t('landing:compare.rLeads'), t('landing:compare.myLeads'), false, NA],
      [t('landing:compare.rFree'), t('landing:compare.myFree'), false, t('landing:compare.tTrialLimited')],
      [t('landing:compare.rDomain'), t('landing:compare.myWl'), true, t('landing:compare.tHigherPlans')],
      [t('landing:compare.rVr'), t('landing:compare.myVr'), true, t('landing:compare.tSupported')],
    ],
  };
  const VS_NAMES: Record<string, string> = {
    matterport: 'Matterport', cloudpano: 'CloudPano', kuula: 'Kuula', hugepano: 'HugePano',
  };

  // ── Motion, WebGL and scroll behaviour ──────────────────────────────
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointer = window.matchMedia('(pointer: fine)').matches;
    /** Phones can't hold this many GL contexts; see the file header. */
    const light = window.matchMedia('(max-width: 999px)').matches;

    const ctx = gsap.context(() => {
      const q = <T extends Element = HTMLElement>(sel: string) => root.querySelector(sel) as T | null;
      const qa = (sel: string) => Array.from(root.querySelectorAll(sel));

      // ── Skybox registry: DOM markers projected onto the 3D world ──
      const registry: { pano: Panorama; items: { el: HTMLElement; u: number; v: number }[]; sync?: Panorama }[] = [];
      const disposers: (() => void)[] = [];

      const unregister = (pano: Panorama) => {
        for (let i = registry.length - 1; i >= 0; i--) {
          if (registry[i].pano === pano || registry[i].sync === pano) {
            registry[i].items.forEach((it) => it.el.classList.remove('vis'));
            registry.splice(i, 1);
          }
        }
      };

      const spawnMarkers = (container: Element | null, spots?: AiHotspot[]) => {
        const items: { el: HTMLElement; u: number; v: number }[] = [];
        if (!container) return items;
        (spots || []).forEach((h) => {
          const meta = TYPE_META[h.type] || TYPE_META.info;
          const el = document.createElement('div');
          el.className = 'sky-marker';
          el.style.setProperty('--mc', meta.c);
          el.innerHTML = `<span class="sky-marker-dot">${meta.ic}</span><span class="sky-marker-label">${h.title}</span>`;
          container.appendChild(el);
          items.push({ el, u: h.x, v: h.y });
        });
        return items;
      };

      let markerRaf = 0;
      const markerLoop = () => {
        markerRaf = requestAnimationFrame(markerLoop);
        registry.forEach((entry) => {
          if (entry.sync) {
            const p = entry.sync.getYawPitch();
            entry.pano.setYawPitch(p.yaw, p.pitch);
          }
          entry.items.forEach((it) => {
            const p = entry.pano.project(it.u, it.v);
            it.el.classList.toggle('vis', p.visible);
            if (p.visible) {
              it.el.style.left = `${p.x}px`;
              it.el.style.top = `${p.y}px`;
            }
          });
        });
      };
      markerLoop();
      disposers.push(() => cancelAnimationFrame(markerRaf));

      const watchActive = (pano: Panorama, el: Element) => {
        const io = new IntersectionObserver(
          (es) => es.forEach((e) => pano.setActive(e.isIntersecting)),
          { rootMargin: '80px' },
        );
        io.observe(el);
        disposers.push(() => io.disconnect());
      };

      /** Desktop builds every viewer up front; phones build/tear down. */
      const mountSky = (el: Element, build: () => { dispose: () => void } | null) => {
        if (!light) {
          const made = build();
          if (made) disposers.push(() => made.dispose());
          return;
        }
        let live: { dispose: () => void } | null = null;
        const io = new IntersectionObserver(
          (es) => es.forEach((e) => {
            if (e.isIntersecting && !live) live = build();
            else if (!e.isIntersecting && live) { live.dispose(); live = null; }
          }),
          { rootMargin: '35%' },
        );
        io.observe(el);
        disposers.push(() => { io.disconnect(); live?.dispose(); });
      };

      // ── Hero panorama — scenery, never a widget ──
      const heroCanvas = q<HTMLCanvasElement>('#lpHero');
      let heroPano: Panorama | null = null;
      if (heroCanvas) {
        heroPano = createPanorama(heroCanvas, '/landing/img/pano-1-8k.webp', {
          initialYaw: 0.4, initialPitch: -0.05,
          autoRotateSpeed: reduced ? 0 : 0.00009, idleDelayMs: 0, interactive: false,
          // Feeds the intro's counter — see `ready` above.
          onLoad: () => markReadyRef.current('pano'),
          onError: () => markReadyRef.current('pano'),
        });
        if (heroPano) {
          watchActive(heroPano, heroCanvas);
          const hp = heroPano;
          disposers.push(() => hp.dispose());
        } else {
          // No WebGL at all — nothing to wait for.
          markReadyRef.current('pano');
        }
      }

      // ── Secondary skyboxes, fed by the AI-generated hotspot map ──
      fetch('/landing/data/hotspots.json')
        .then((r) => r.json())
        .catch(() => ({}))
        .then((SPOTS: Record<string, AiHotspot[]>) => {
          markReadyRef.current('spots');
          // Hero com os pontos DE VERDADE (gerados pelo mesmo assistente do
          // editor sobre esta panorâmica): mostrar o produto funcionando é
          // melhor do que descrevê-lo. Mesma projeção dos skyboxes de setor.
          if (heroPano) {
            const heroItems = spawnMarkers(q('#lpHeroMarkers'), SPOTS['hero']);
            if (heroItems.length) registry.push({ pano: heroPano, items: heroItems });
          }
          qa('.sky-stage').forEach((stage, idx) => {
            const canvas = stage.querySelector('canvas');
            if (!canvas) return;
            const items = spawnMarkers(stage.querySelector('.sky-markers'), SPOTS[stage.getAttribute('data-spots') || '']);
            mountSky(stage, () => {
              const sky = createPanorama(canvas as HTMLCanvasElement, stage.getAttribute('data-sky') || '', {
                initialYaw: parseFloat(stage.getAttribute('data-yaw') || '0'),
                initialPitch: -0.02,
                autoRotateSpeed: reduced ? 0 : 0.000055 * (idx % 2 === 0 ? 1 : -1),
                idleDelayMs: 0, interactive: false,
              });
              if (!sky) return null;
              registry.push({ pano: sky, items });
              if (!light) watchActive(sky, stage);
              return { dispose: () => { unregister(sky); sky.dispose(); } };
            });
          });

          // White-label window
          const wlCanvas = q<HTMLCanvasElement>('#lpWl');
          if (wlCanvas) {
            const wlItems = spawnMarkers(q('#lpWlMarkers'), SPOTS['sector-imoveis']);
            mountSky(wlCanvas, () => {
              const sky = createPanorama(wlCanvas, '/landing/img/sector-imoveis.webp', {
                initialYaw: 1.1, initialPitch: -0.03,
                autoRotateSpeed: reduced ? 0 : 0.00005, idleDelayMs: 0, interactive: false,
              });
              if (!sky) return null;
              registry.push({ pano: sky, items: wlItems });
              if (!light) watchActive(sky, wlCanvas);
              return { dispose: () => { unregister(sky); sky.dispose(); } };
            });
          }

          // Before/after — two synced spheres split by the slider
          const afterC = q<HTMLCanvasElement>('#lpAfter');
          const beforeC = q<HTMLCanvasElement>('#lpBefore');
          const baStage = q('#lpBa');
          if (afterC && beforeC && baStage) {
            const afterItems = spawnMarkers(q('#lpBaMarkers'), SPOTS['ai-staging-after']);
            // Frames the decorated half of the room rather than a wall.
            // Derived, not guessed: the panorama maps uv u to φ = 2π(1−u),
            // and a point sits dead centre when yaw = φ + π/2. Feeding it the
            // midpoint of this take's own hotspots (u ≈ 0.58) gives 4.21.
            const START = 4.21;
            mountSky(baStage, () => {
              const after = createPanorama(afterC, '/landing/img/ai-staging-after.webp', {
                initialYaw: START, initialPitch: -0.04,
                autoRotateSpeed: reduced ? 0 : 0.00004, idleDelayMs: 0, interactive: false,
              });
              const before = createPanorama(beforeC, '/landing/img/ai-staging-before.webp', {
                initialYaw: START, initialPitch: -0.04, autoRotateSpeed: 0, idleDelayMs: 0, interactive: false,
              });
              if (!after || !before) return null;
              registry.push({ pano: after, items: afterItems });
              registry.push({ pano: before, items: [], sync: after });
              if (!light) { watchActive(after, baStage); watchActive(before, baStage); }
              return {
                dispose: () => { unregister(after); unregister(before); after.dispose(); before.dispose(); },
              };
            });
          }
        });

      // ── Before/after drag handle ──
      const slider = q('#lpBa');
      const beforeWrap = q('#lpBaBefore');
      const handle = q('#lpBaHandle');
      if (slider && beforeWrap && handle) {
        const setPct = (p: number) => {
          const v = Math.max(0, Math.min(100, p));
          (beforeWrap as HTMLElement).style.clipPath = `inset(0 ${100 - v}% 0 0)`;
          (handle as HTMLElement).style.left = `${v}%`;
          handle.setAttribute('aria-valuenow', String(Math.round(v)));
        };
        let dragging = false;
        const pctFrom = (e: PointerEvent) => {
          const r = slider.getBoundingClientRect();
          return ((e.clientX - r.left) / r.width) * 100;
        };
        const onDown = (e: PointerEvent) => { dragging = true; setPct(pctFrom(e)); };
        const onMove = (e: PointerEvent) => { if (dragging) setPct(pctFrom(e)); };
        const onUp = () => { dragging = false; };
        const onKey = (e: KeyboardEvent) => {
          const cur = parseFloat((handle as HTMLElement).style.left) || 50;
          if (e.key === 'ArrowLeft') { setPct(cur - 5); e.preventDefault(); }
          if (e.key === 'ArrowRight') { setPct(cur + 5); e.preventDefault(); }
        };
        slider.addEventListener('pointerdown', onDown as EventListener);
        window.addEventListener('pointermove', onMove as EventListener);
        window.addEventListener('pointerup', onUp);
        handle.addEventListener('keydown', onKey as EventListener);
        setPct(50);
        disposers.push(() => {
          slider.removeEventListener('pointerdown', onDown as EventListener);
          window.removeEventListener('pointermove', onMove as EventListener);
          window.removeEventListener('pointerup', onUp);
          handle.removeEventListener('keydown', onKey as EventListener);
        });
      }

      // ── Live tour embed: click to interact (desktop) / open full screen ──
      const embedBody = q('#lpEmbed');
      const shield = q('#lpShield');
      if (embedBody && shield) {
        if (light) {
          const frame = embedBody.querySelector('iframe');
          if (frame) (frame as HTMLElement).style.pointerEvents = 'none';
          shield.addEventListener('click', () => {
            window.open(`https://my360.site/p/${DEMO_SHARE_ID}`, '_blank', 'noopener');
          });
        } else {
          shield.addEventListener('click', () => shield.classList.add('off'));
          embedBody.addEventListener('mouseenter', () => root.classList.add('cursor-off'));
          embedBody.addEventListener('mouseleave', () => {
            shield.classList.remove('off');
            root.classList.remove('cursor-off');
          });
          const io = new IntersectionObserver(
            (es) => es.forEach((e) => { if (!e.isIntersecting) shield.classList.remove('off'); }),
            { threshold: 0.3 },
          );
          io.observe(embedBody);
          disposers.push(() => io.disconnect());
        }
      }

      // ── Ambient dust ──
      const dust = q<HTMLCanvasElement>('#lpDust');
      let scrollVel = 0;
      if (dust && !reduced) {
        const c = dust.getContext('2d')!;
        const parts = Array.from({ length: window.innerWidth < 760 ? 24 : 56 }, () => ({
          x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight,
          r: Math.random() * 1.4 + 0.4, vy: Math.random() * 0.12 + 0.03,
          vx: (Math.random() - 0.5) * 0.05, a: Math.random() * 0.5 + 0.15,
        }));
        const resize = () => { dust.width = window.innerWidth; dust.height = window.innerHeight; };
        resize();
        window.addEventListener('resize', resize);
        let dustRaf = 0;
        const tick = () => {
          dustRaf = requestAnimationFrame(tick);
          c.clearRect(0, 0, dust.width, dust.height);
          c.fillStyle = '#ffffff';
          parts.forEach((p) => {
            p.y -= p.vy + Math.min(Math.abs(scrollVel) * 0.02, 1.6);
            p.x += p.vx;
            if (p.y < -4) { p.y = dust.height + 4; p.x = Math.random() * dust.width; }
            c.globalAlpha = p.a;
            c.beginPath();
            c.arc(p.x, p.y, p.r, 0, Math.PI * 2);
            c.fill();
          });
          scrollVel *= 0.9;
        };
        tick();
        disposers.push(() => { cancelAnimationFrame(dustRaf); window.removeEventListener('resize', resize); });
      }

      // ── Custom cursor + magnetic buttons (fine pointer only) ──
      if (!reduced && finePointer) {
        root.classList.add('has-custom-cursor');
        const dot = q('#lpCurDot');
        const ring = q('#lpCurRing');
        const dx = gsap.quickTo(dot, 'x', { duration: 0.1, ease: 'power3' });
        const dy = gsap.quickTo(dot, 'y', { duration: 0.1, ease: 'power3' });
        const rx = gsap.quickTo(ring, 'x', { duration: 0.45, ease: 'power3' });
        const ry = gsap.quickTo(ring, 'y', { duration: 0.45, ease: 'power3' });
        const onPointer = (e: PointerEvent) => { dx(e.clientX); dy(e.clientY); rx(e.clientX); ry(e.clientY); };
        window.addEventListener('pointermove', onPointer);
        const hoverSel = 'a, button, .magnet, .cell, .price, .dl-card, .sector-card';
        const over = (e: Event) => { if ((e.target as Element).closest(hoverSel)) root.classList.add('cur-hover'); };
        const out = (e: Event) => { if ((e.target as Element).closest(hoverSel)) root.classList.remove('cur-hover'); };
        root.addEventListener('pointerover', over);
        root.addEventListener('pointerout', out);
        disposers.push(() => {
          window.removeEventListener('pointermove', onPointer);
          root.removeEventListener('pointerover', over);
          root.removeEventListener('pointerout', out);
        });

        qa('.magnet').forEach((el) => {
          el.addEventListener('pointermove', (ev) => {
            const e = ev as PointerEvent;
            const r = el.getBoundingClientRect();
            const mx = e.clientX - r.left, my = e.clientY - r.top;
            (el as HTMLElement).style.setProperty('--mx', `${mx}px`);
            (el as HTMLElement).style.setProperty('--my', `${my}px`);
            gsap.to(el, { x: (mx / r.width - 0.5) * 10, y: (my / r.height - 0.5) * 10, duration: 0.3, ease: 'power2.out' });
          });
          el.addEventListener('pointerleave', () => gsap.to(el, { x: 0, y: 0, duration: 0.4, ease: 'power3.out' }));
        });
        qa('.magnet-cell').forEach((el) => {
          el.addEventListener('pointermove', (ev) => {
            const e = ev as PointerEvent;
            const r = el.getBoundingClientRect();
            (el as HTMLElement).style.setProperty('--mx', `${e.clientX - r.left}px`);
            (el as HTMLElement).style.setProperty('--my', `${e.clientY - r.top}px`);
          });
        });
      }

      if (reduced) {
        qa('.reveal').forEach((el) => { (el as HTMLElement).style.opacity = '1'; });
        return;
      }

      // ── Lenis drives the scroll; GSAP's ticker drives Lenis ──
      const lenis = new Lenis({ duration: 1.1, smoothWheel: true, touchMultiplier: 1.1 });
      lenis.on('scroll', (e: { velocity?: number }) => {
        ScrollTrigger.update();
        scrollVel = e.velocity ?? 0;
      });
      const raf = (time: number) => lenis.raf(time * 1000);
      gsap.ticker.add(raf);
      gsap.ticker.lagSmoothing(0);
      disposers.push(() => { gsap.ticker.remove(raf); lenis.destroy(); });

      gsap.defaults({ ease: 'power3.out' });

      // Hero entrance — per-character rise. Starts PAUSED: the intro's
      // curtains call onReveal, and only then does this play, so the hero
      // is already animating as it's uncovered.
      const h1 = q('#lpH1');
      const tl = gsap.timeline({ paused: true });
      heroTlRef.current = tl;
      tl.set('.hero-content', { opacity: 1 });
      if (h1) {
        SplitText.create(h1, {
          type: 'chars', charsClass: 'ch', autoSplit: true,
          onSplit: (self) => gsap.to(self.chars, { opacity: 1, y: 0, rotateX: 0, duration: 0.85, stagger: 0.014, ease: 'power3.out' }),
        });
      }
      tl.add('h1')
        .from('.hero-ctas', { opacity: 0, y: 14, duration: 0.6 }, 'h1+=0.55')
        .to('.scroll-cue', { opacity: 1, duration: 0.5 }, '-=0.3')
        .set('.hero-ctas', { clearProps: 'opacity' });

      // Generic scroll reveal
      qa('.section .reveal, .tension .reveal, .scene .reveal, .final .reveal').forEach((el) => {
        gsap.fromTo(el, { opacity: 0, y: 20 }, {
          opacity: 1, y: 0, duration: 0.7, ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 90%', once: true },
        });
      });

      // Ambient blob moods per section
      const wgs = qa('.wg');
      const moods: [string, number[]][] = [
        ['#hero', [0.85, 0.5, 0.25, 0.15]], ['#tension', [0.5, 0.3, 0.55, 0.25]],
        ['#who', [0.3, 0.55, 0.2, 0.35]], ['#how', [0.4, 0.2, 0.5, 0.25]],
        ['#editor', [0.25, 0.45, 0.3, 0.2]], ['#ai', [0.45, 0.55, 0.35, 0.4]],
        ['#vr', [0.3, 0.5, 0.4, 0.55]], ['#whitelabel', [0.55, 0.35, 0.45, 0.3]],
        ['#tours', [0.5, 0.25, 0.4, 0.2]], ['#features', [0.3, 0.4, 0.25, 0.45]],
        ['#compare', [0.5, 0.3, 0.35, 0.45]], ['#pricing', [0.45, 0.3, 0.5, 0.2]],
        ['#downloads', [0.35, 0.5, 0.3, 0.4]], ['#faq', [0.25, 0.35, 0.2, 0.3]],
        ['#final', [0.9, 0.55, 0.6, 0.35]],
      ];
      moods.forEach(([trigger, vals]) => {
        const apply = () => gsap.to(wgs, { opacity: (i: number) => vals[i], duration: 1.4, ease: 'power2.out', overwrite: 'auto' });
        ScrollTrigger.create({ trigger, start: 'top 60%', end: 'bottom 40%', onEnter: apply, onEnterBack: apply });
      });

      // HUD: progress, chapter, dots
      const fill = q('#lpProgress');
      const chapter = q('#lpChapter');
      const dots = q('#lpDots');
      // Wipe first: StrictMode runs effects twice in dev, and any refresh
      // would otherwise append a second full set of dots.
      if (dots) dots.innerHTML = '';
      qa('[data-chapter]').forEach((sec, i) => {
        if (dots) {
          const b = document.createElement('button');
          b.className = 'hud-dot';
          b.type = 'button';
          b.setAttribute('aria-label', sec.getAttribute('data-chapter-label') || '');
          b.addEventListener('click', () => lenis.scrollTo(sec as HTMLElement, { duration: 1.1 }));
          dots.appendChild(b);
        }
        ScrollTrigger.create({
          trigger: sec, start: 'top 55%', end: 'bottom 55%',
          onToggle: (self) => {
            if (!self.isActive || !chapter || !dots) return;
            chapter.textContent = sec.getAttribute('data-chapter');
            dots.querySelectorAll('.hud-dot').forEach((d, di) => d.classList.toggle('on', di === i));
          },
        });
      });
      if (fill) {
        ScrollTrigger.create({
          trigger: document.body, start: 'top top', end: 'bottom bottom',
          onUpdate: (self) => { (fill as HTMLElement).style.width = `${self.progress * 100}%`; },
        });
      }
      const footer = q('#lpFooter');
      if (footer) {
        const io = new IntersectionObserver(
          (es) => es.forEach((e) => root.classList.toggle('at-footer', e.isIntersecting)),
          { threshold: 0.15 },
        );
        io.observe(footer);
        disposers.push(() => io.disconnect());
      }

      // ── Desktop choreography. Pinned timelines are created in DOCUMENT
      //    ORDER: ScrollTrigger computes pin spacers in creation order, and
      //    registering a later section first makes it pin at a pre-spacer
      //    position (a panel landing on top of the wrong section). ──
      const mm = gsap.matchMedia();

      mm.add('(min-width: 1000px)', () => {
        const steps = ['#lpStep0', '#lpStep1', '#lpStep2'].map((s) => q(s));
        if (steps.some((s) => !s)) return;
        const glows = steps.map((s) => s!.querySelector('.how-glow'));
        const nums = steps.map((s) => s!.querySelector('.how-num'));
        const line = q('#lpHowLine');
        const DIM = 'brightness(0.3) saturate(0.35)';

        gsap.set(glows, { opacity: 0 });
        // Dim via FILTER, never opacity — a translucent card lets the
        // connector line show through it.
        gsap.set(steps, { filter: DIM, scale: 0.95 });
        gsap.set(nums, { color: 'rgba(243,244,241,0.4)' });
        if (line) gsap.set(line, { drawSVG: '0%' });

        const how = gsap.timeline({
          scrollTrigger: { trigger: '.how-stage', start: 'center center', end: '+=230%', scrub: 0.6, pin: true, anticipatePin: 1 },
        });
        if (line) how.to(line, { drawSVG: '100%', duration: 0.72, ease: 'none' }, 0);
        steps.forEach((step, i) => {
          const pos = i * 0.3;
          how.to(step, { filter: 'brightness(1) saturate(1)', scale: 1.05, y: -8, duration: 0.1, ease: 'power2.out' }, pos)
            .to(glows[i], { opacity: 1, duration: 0.1 }, pos)
            .to(nums[i], { color: '#2DD4BF', duration: 0.1 }, pos)
            .to(step, { y: 0, duration: 0.14, ease: 'power2.inOut' }, pos + 0.1);
          if (i < steps.length - 1) {
            how.to(step, { filter: 'brightness(0.5) saturate(0.6)', scale: 1, duration: 0.08 }, pos + 0.24)
              .to(glows[i], { opacity: 0, duration: 0.1 }, pos + 0.24)
              .to(nums[i], { color: 'rgba(243,244,241,0.4)', duration: 0.1 }, pos + 0.24);
          }
        });
        how.to({}, { duration: 0.18 });
        return () => { how.scrollTrigger?.kill(); };
      });

      // VR — o headset com o skybox VIVO nos furos das lentes (calibrado
      // no /lab/meta-lens.html). Desktop: pinned, o scroll dirige a troca
      // de cena. Mobile/tablet: mesma piscada em ritmo próprio enquanto a
      // seção está visível — pin em tela pequena é scroll-jacking.
      const VR_SHOTS = [
        '/landing/img/pano-1-8k.webp', '/landing/img/ai-staging-after.webp',
        '/landing/img/sector-hotel.webp', '/landing/img/sector-museu.webp',
      ];
      const headsetEl = q('#lpVrHeadset');
      const lensViewers: (LensViewer | null)[] = [];
      if (headsetEl) {
        const canvases = Array.from(headsetEl.querySelectorAll('canvas')) as HTMLCanvasElement[];
        // 0.055 rad entre os olhos — o desvio que vende o estéreo.
        lensViewers.push(createLensViewer(canvases[0], 0), createLensViewer(canvases[1], 0.055));
        const first = new Image();
        first.onload = () => lensViewers.forEach((v) => v?.setImage(first));
        first.src = VR_SHOTS[0];
        // Fora da viewport o GL dorme — telefones não pagam por lentes
        // que ninguém está vendo.
        const io = new IntersectionObserver(
          ([e]) => lensViewers.forEach((v) => v?.setActive(e.isIntersecting)),
          { rootMargin: '20%' },
        );
        io.observe(headsetEl);
        disposers.push(() => { io.disconnect(); lensViewers.forEach((v) => v?.dispose()); });
      }
      // Piscada: pálpebra desce, a TEXTURA troca coberta, pálpebra sobe.
      let lensShown = 0;
      let lensBusy = false;
      const lensBlinkTo = (idx: number) => {
        if (lensBusy || idx === lensShown || !lensViewers.length) return;
        lensBusy = true;
        const lids = qa('.vr-lid');
        const im = new Image();
        im.src = VR_SHOTS[idx];
        const swap = () => lensViewers.forEach((v) => v?.setImage(im));
        gsap.timeline({ onComplete: () => { lensBusy = false; } })
          .to(lids, { scaleY: 1, duration: 0.16, ease: 'power2.in' })
          .add(() => {
            lensShown = idx;
            if (im.complete) swap();
            else im.onload = swap;
          })
          .to(lids, { scaleY: 0, duration: 0.26, ease: 'power2.out' }, '+=0.06');
      };
      mm.add('(min-width: 1000px)', () => {
        if (!headsetEl) return;
        const st = ScrollTrigger.create({
          trigger: '#vr', start: 'center center', end: `+=${VR_SHOTS.length * 55}%`,
          pin: true, anticipatePin: 1,
          onUpdate: (self) => lensBlinkTo(Math.min(VR_SHOTS.length - 1, Math.floor(self.progress * VR_SHOTS.length))),
        });
        return () => st.kill();
      });
      mm.add('(max-width: 999px)', () => {
        const vrSec = q('#vr');
        if (!headsetEl || !vrSec || reduced) return;
        let timer: number | null = null;
        const io = new IntersectionObserver(
          ([e]) => {
            if (e.isIntersecting) {
              if (timer == null) timer = window.setInterval(() => lensBlinkTo((lensShown + 1) % VR_SHOTS.length), 2600);
            } else if (timer != null) {
              clearInterval(timer);
              timer = null;
            }
          },
          { threshold: 0.35 },
        );
        io.observe(vrSec);
        return () => {
          io.disconnect();
          if (timer != null) clearInterval(timer);
        };
      });

      // White-label — the address bar is the proof
      mm.add('(min-width: 1000px)', () => {
        const urlA = q('#lpUrlA'), urlB = q('#lpUrlB');
        const badgeA = q('#lpBadgeA'), badgeB = q('#lpBadgeB');
        if (!urlA || !urlB) return;
        const wl = gsap.timeline({
          scrollTrigger: { trigger: '#whitelabel', start: 'top top', end: '+=100%', scrub: 0.6, pin: true, anticipatePin: 1 },
        });
        wl.to({}, { duration: 0.25 })
          .to(urlA, { yPercent: -100, opacity: 0, duration: 0.22, ease: 'power2.in' }, 0.3)
          .fromTo(urlB, { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.24, ease: 'power2.out' }, 0.42)
          .to(badgeA, { opacity: 0, scale: 0.9, duration: 0.15 }, 0.6)
          .fromTo(badgeB, { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 0.2 }, 0.68)
          .to({}, { duration: 0.2 });
        return () => { wl.scrollTrigger?.kill(); };
      });

      // Section hand-offs. Pinned sections are excluded: a transformed
      // ancestor hijacks position:fixed (the pin flew off-screen), and the
      // exit fade measured past the pin spacer darkened the panel while its
      // last beat was still being scrubbed.
      mm.add('(min-width: 1000px)', () => {
        const tweens: gsap.core.Tween[] = [];
        qa('.section, .tension, .final').forEach((sec) => {
          if (sec.hasAttribute('data-pinned')) return;
          const inner = sec.querySelector('.wrap');
          if (!inner) return;
          tweens.push(gsap.fromTo(inner, { y: 60, autoAlpha: 0.35 }, {
            y: 0, autoAlpha: 1, ease: 'none',
            scrollTrigger: { trigger: sec, start: 'top bottom', end: 'top 55%', scrub: 0.8 },
          }));
          tweens.push(gsap.fromTo(inner, { y: 0, autoAlpha: 1 }, {
            y: -50, autoAlpha: 0.25, ease: 'none',
            scrollTrigger: { trigger: sec, start: 'bottom 75%', end: 'bottom top', scrub: 0.8 },
          }));
        });
        return () => tweens.forEach((tw) => { tw.scrollTrigger?.kill(); tw.kill(); });
      });

      // Sem encaixe de rolagem. Existia um snap que puxava a página para o
      // começo da seção quando a rolagem parava perto de uma fronteira. Por
      // mais suave que fosse, ele tira da pessoa o controle do próprio scroll:
      // você para onde quer ler e a página decide que era em outro lugar. Um
      // site que corrige a sua rolagem é um site brigando com você.

      ScrollTrigger.sort();
      requestAnimationFrame(() => ScrollTrigger.refresh());

      disposers.push(() => mm.revert());
      return () => disposers.forEach((d) => d());
    }, rootRef);

    return () => ctx.revert();
  }, []);

  // The landing owns the page background while mounted.
  useEffect(() => {
    document.body.classList.add('lp-body');
    return () => document.body.classList.remove('lp-body');
  }, []);

  // The hero holds until the curtains start parting.
  useEffect(() => {
    if (revealed) heroTlRef.current?.play();
  }, [revealed]);

  const [faqOpen, setFaqOpen] = useState(0);
  // Keys are enumerated, not built from a template: the repo generates
  // compile-time types for every i18n key, and a dynamic key defeats that.
  const faqs = [
    { q: t('landing:faq.q1'), a: t('landing:faq.a1') },
    { q: t('landing:faq.q2'), a: t('landing:faq.a2') },
    { q: t('landing:faq.q3'), a: t('landing:faq.a3') },
    { q: t('landing:faq.q4'), a: t('landing:faq.a4') },
    { q: t('landing:faq.q5'), a: t('landing:faq.a5') },
    { q: t('landing:faq.q6'), a: t('landing:faq.a6') },
  ];

  // O Google mostra FAQ expandida no resultado quando ela vem marcada.
  // Deriva do MESMO array renderizado — schema e tela nunca divergem, que
  // é o que o Google trata como marcação enganosa.
  useJsonLd('landing-faq', {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': 'https://my360.site/#faq',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  });

  const vrPoints = [
    { t: t('landing:vr.p1'), d: t('landing:vr.p1d') },
    { t: t('landing:vr.p2'), d: t('landing:vr.p2d') },
    { t: t('landing:vr.p3'), d: t('landing:vr.p3d') },
  ];

  const featureList = [
    { t: t('landing:features.f1'), d: t('landing:features.f1d') },
    { t: t('landing:features.f2'), d: t('landing:features.f2d') },
    { t: t('landing:features.f3'), d: t('landing:features.f3d') },
    { t: t('landing:features.f4'), d: t('landing:features.f4d') },
    { t: t('landing:features.f5'), d: t('landing:features.f5d') },
    { t: t('landing:features.f6'), d: t('landing:features.f6d') },
  ];

  const sectors = [
    { key: 'sector-imoveis', img: '/landing/img/sector-imoveis.webp', yaw: 0.4, name: t('landing:sectors.realEstate'), desc: t('landing:sectors.realEstateDesc') },
    { key: 'sector-hotel', img: '/landing/img/sector-hotel.webp', yaw: 2.1, name: t('landing:sectors.hotel'), desc: t('landing:sectors.hotelDesc') },
    { key: 'sector-varejo', img: '/landing/img/sector-varejo.webp', yaw: 4.2, name: t('landing:sectors.retail'), desc: t('landing:sectors.retailDesc') },
    { key: 'sector-museu', img: '/landing/img/sector-museu.webp', yaw: 1.2, name: t('landing:sectors.museum'), desc: t('landing:sectors.museumDesc') },
  ];

  const EDITOR_TYPE_LABEL: Record<string, string> = {
    navigation: t('landing:editor.navigation'), text: t('landing:editor.text'),
    image: t('landing:editor.image'), link: t('landing:editor.link'),
    video: t('landing:editor.video'), contact: t('landing:editor.contact'),
  };

  const CHECK = <span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2"><path d="M4 12.5l5 5L20 6.5" strokeLinecap="round" strokeLinejoin="round" /></svg></span>;
  const DASH = <span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2"><path d="M5 12h14" strokeLinecap="round" /></svg></span>;

  return (
    <div className="lp" ref={rootRef}>
      <BrandLoader variant="intro" progress={progress} onReveal={() => setRevealed(true)} />

      <div id="world" aria-hidden="true">
        <div className="wg wg1" /><div className="wg wg2" /><div className="wg wg3" /><div className="wg wg4" />
        <canvas id="lpDust" />
        <div className="grain" /><div className="vignette" />
      </div>

      <div className="cursor-dot" id="lpCurDot" /><div className="cursor-ring" id="lpCurRing" />

      <div className="hud">
        <div className="hud-progress"><span id="lpProgress" /></div>
        <div className="hud-chapter" id="lpChapter">00</div>
        <div className="hud-dots" id="lpDots" />
      </div>

      <header className="nav" id="lpNav">
        <div className="nav-inner">
          <a href="#top" className="brand">
            <img className="brand-logo" src="/logo.png" alt="" width={26} height={26} />
            <span className="brand-name">my360</span>
          </a>
          <nav className="nav-links">
            <a href="#how">{t('landing:nav.how')}</a>
            <a href="#editor">{t('landing:nav.editor')}</a>
            <a href="#tours">{t('landing:nav.demo')}</a>
            <a href="#compare">{t('landing:nav.compare')}</a>
            <a href="#pricing">{t('landing:nav.plans')}</a>
          </nav>
          <div className="nav-actions">
            <LangSwitcher />
            {/* Sem toggle de tema aqui: a landing tem um look só (escuro);
                claro/escuro é escolha de quem usa o sistema, dentro do app. */}
            {isAuthenticated ? (
              <a href={appUrl("/dashboard")} className="btn sm btn-primary">{t('landing:nav.openDashboard')}</a>
            ) : (
              <>
                <a href={appUrl("/login")} className="btn sm">{t('landing:nav.signIn')}</a>
                <a href={appUrl("/register")} className="btn sm btn-primary">{t('landing:nav.startFree')}</a>
              </>
            )}
          </div>
        </div>
      </header>

      <main id="top">
        {/* ── Hero ── */}
        <section className="hero" id="hero" data-chapter="00" data-chapter-label={t('landing:nav.how')}>
          <canvas id="lpHero" className="pano-canvas" aria-hidden="true" />
          <div className="hero-scrim" />
          {/* Marcadores reais sobre o pano do hero — acima do scrim,
              abaixo do texto (ordem do DOM resolve o empilhamento). */}
          <div className="sky-markers" id="lpHeroMarkers" aria-hidden="true" />
          {/* Deliberately bare: headline and the two actions. Everything
              the eyebrow/sub/friction lines used to say is said better by
              the sections below, and the panorama needs room to breathe. */}
          <div className="hero-content">
            <h1 className="hero-h1" id="lpH1">
              <span className="line">{t('landing:hero.line1')}</span>
              <span className="line">{t('landing:hero.line2')} <span className="accent">{t('landing:hero.accent')}</span></span>
            </h1>
            <div className="hero-ctas">
              <a href={appUrl("/register")} className="btn btn-primary lg magnet">{t('landing:hero.ctaPrimary')}</a>
              <a href="#how" className="btn lg magnet">{t('landing:hero.ctaSecondary')}</a>
            </div>
          </div>
          <div className="scroll-cue" aria-hidden="true"><span /></div>
        </section>

        {/* ── The problem ── */}
        <section className="tension" id="tension" data-chapter="01" data-chapter-label={t('landing:tension.line')}>
          <div className="wrap tension-inner">
            <p className="tension-line reveal">{t('landing:tension.line')}</p>
            <p className="tension-payoff reveal">{t('landing:tension.payoff')}</p>
          </div>
        </section>

        {/* ── Sectors ── */}
        <section id="who" className="section" data-chapter="02" data-chapter-label={t('landing:sectors.title')}>
          <div className="wrap">
            <div className="section-head center reveal">
              <span className="eyebrow">{t('landing:sectors.eyebrow')}</span>
              <h2>{t('landing:sectors.title')}</h2>
            </div>
            <div className="sector-grid">
              {sectors.map((s) => (
                <div key={s.key} className="sector-card sky-stage reveal" data-sky={s.img} data-spots={s.key} data-yaw={s.yaw}>
                  <canvas className="sky-canvas" aria-hidden="true" />
                  <div className="sky-markers" aria-hidden="true" />
                  <div className="sector-veil" />
                  <div className="sector-label"><h3>{s.name}</h3><p>{s.desc}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── How it works ── */}
        <section id="how" className="section alt how-section" data-pinned data-chapter="03" data-chapter-label={t('landing:nav.how')}>
          <div className="wrap">
            <div className="section-head reveal">
              <span className="eyebrow">{t('landing:how.eyebrow')}</span>
              <h2>{t('landing:how.title')}</h2>
              <p>{t('landing:how.sub')}</p>
            </div>
            <div className="how-stage">
              <svg className="how-line" viewBox="0 0 100 1" preserveAspectRatio="none" aria-hidden="true">
                <line x1="0" y1="0.5" x2="100" y2="0.5" className="how-line-track" strokeWidth="1" />
                <line x1="0" y1="0.5" x2="100" y2="0.5" className="how-line-draw" strokeWidth="1.5" id="lpHowLine" />
              </svg>
              <div className="how-grid">
                {[
                  { id: 'lpStep0', img: 'how-upload', n: '01', t: t('landing:how.s1'), d: t('landing:how.s1d') },
                  { id: 'lpStep1', img: 'how-points', n: '02', t: t('landing:how.s2'), d: t('landing:how.s2d') },
                  { id: 'lpStep2', img: 'how-publish', n: '03', t: t('landing:how.s3'), d: t('landing:how.s3d') },
                ].map((s) => (
                  <div className="how-step" id={s.id} key={s.id}>
                    <span className="how-glow" />
                    <div className="how-visual"><img src={`/landing/img/${s.img}.webp`} alt="" loading="lazy" /></div>
                    <span className="how-num">{s.n}</span>
                    <h3>{s.t}</h3>
                    <p>{s.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── The editor, demonstrating itself ── */}
        <section id="editor" className="scene" data-chapter="04" data-chapter-label={t('landing:nav.editor')}>
          <div className="wrap scene-wrap wide-stage">
            <div className="scene-copy reveal">
              <span className="eyebrow">{t('landing:editor.eyebrow')}</span>
              <h2>{t('landing:editor.title')}</h2>
              <p>{t('landing:editor.sub')}</p>
              <ul className="scene-legend">
                {(['navigation', 'text', 'image', 'link', 'video', 'contact'] as const).map((k) => (
                  <li key={k}>
                    <span className="dot" style={{ background: TYPE_META[k === 'text' ? 'info' : k === 'link' ? 'url' : k].c }} />
                    {EDITOR_TYPE_LABEL[k]}
                  </li>
                ))}
              </ul>
            </div>
            <div className="scene-stage reveal">
              <div className="editor-live">
                <div className="live-embed-bar">
                  <span className="live-embed-dot" />
                  <span className="live-embed-url">my360.site/demo/editor</span>
                  <span className="live-embed-tag">{t('landing:editor.bar')}</span>
                </div>
                {/* Same-origin, unlike the published-tour embed below: this
                    route is app code, so it has to come from whatever build
                    is serving the landing. Hard-coding production meant the
                    frame went blank whenever prod was a version behind. It
                    still reads the public tour API — run dev with
                    VITE_API_TARGET=https://my360.site if no local API. */}
                <iframe className="editor-live-frame" src="/demo/editor?autopilot=1" title={t('landing:editor.bar')} loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* ── AI staging ── */}
        <section className="scene" id="ai" data-chapter="05" data-chapter-label={t('landing:ai.title')}>
          <div className="wrap scene-full">
            <div className="section-head center reveal">
              <span className="eyebrow">{t('landing:ai.eyebrow')}</span>
              <h2>{t('landing:ai.title')}</h2>
              <p>{t('landing:ai.sub')}</p>
            </div>
            <div className="ba-wide reveal">
              <div className="ba-slider" id="lpBa">
                <div className="ba-layer">
                  <canvas id="lpAfter" className="sky-canvas" aria-hidden="true" />
                  <div className="sky-markers" id="lpBaMarkers" aria-hidden="true" />
                </div>
                <div className="ba-layer ba-before-wrap" id="lpBaBefore">
                  <canvas id="lpBefore" className="sky-canvas" aria-hidden="true" />
                </div>
                <div className="ba-handle" id="lpBaHandle" role="slider" aria-label={t('landing:a11y.compareSlider')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={50} tabIndex={0}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 8l-5 4 5 4M16 8l5 4-5 4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <span className="ba-tag ba-tag-before">{t('landing:ai.before')}</span>
                <span className="ba-tag ba-tag-after">{t('landing:ai.after')}</span>
              </div>
              <p className="ba-prompt"><span>{t('landing:ai.promptLabel')}</span> {t('landing:ai.prompt')}</p>
            </div>
          </div>
        </section>

        {/* ── VR ── */}
        <section id="vr" className="section" data-pinned data-chapter="06" data-chapter-label="VR">
          <div className="wrap">
            <div className="section-head center reveal">
              <span className="eyebrow">{t('landing:vr.eyebrow')}</span>
              <h2>{t('landing:vr.title')}</h2>
              <p>{t('landing:vr.sub')}</p>
            </div>
            <div className="vr-row">
              {/* Headset com o skybox VIVO dentro dos furos das lentes —
                  calibrado no /lab/meta-lens.html. */}
              <div className="vr-headset reveal" id="lpVrHeadset" aria-hidden="true">
                <div className="vr-hl l"><canvas /><span className="glint" /><span className="glass" /><span className="vr-lid" /></div>
                <div className="vr-hl r"><canvas /><span className="glint" /><span className="glass" /><span className="vr-lid" /></div>
                <img className="vr-headset-img" src="/lab/meta.png" alt="" draggable={false} />
              </div>
              <ul className="vr-points reveal">
                {vrPoints.map((p, i) => (
                  <li key={i}><strong>{p.t}</strong> — {p.d}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── White label ── */}
        <section className="scene" id="whitelabel" data-chapter="07" data-chapter-label={t('landing:whitelabel.title')}>
          <div className="wrap scene-wrap reverse">
            <div className="scene-copy reveal">
              <span className="eyebrow">{t('landing:whitelabel.eyebrow')}</span>
              <h2>{t('landing:whitelabel.title')}</h2>
              <p>{t('landing:whitelabel.sub')}</p>
            </div>
            <div className="scene-stage reveal">
              <div className="wl-window">
                <div className="wl-bar">
                  <span className="wl-dot" /><span className="wl-dot" /><span className="wl-dot" />
                  <span className="wl-urlbox">
                    <span className="wl-url wl-url-a" id="lpUrlA">meutour.<strong>my360.site</strong></span>
                    <span className="wl-url wl-url-b" id="lpUrlB">visita.<strong>imobiliariaporto.com.br</strong></span>
                  </span>
                </div>
                <div className="wl-body">
                  <canvas className="sky-canvas" id="lpWl" aria-hidden="true" />
                  <div className="sky-markers" id="lpWlMarkers" aria-hidden="true" />
                  <div className="wl-badge wl-badge-a" id="lpBadgeA"><span className="wl-badge-mark" />my360</div>
                  <div className="wl-badge brand wl-badge-b" id="lpBadgeB"><span className="wl-badge-mark brand" />Imobiliária Porto</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Live tour ── */}
        <section id="tours" className="section alt" data-chapter="08" data-chapter-label={t('landing:live.title')}>
          <div className="wrap">
            <div className="section-head reveal">
              <span className="eyebrow">{t('landing:live.eyebrow')}</span>
              <h2>{t('landing:live.title')}</h2>
              <p>{t('landing:live.sub')}</p>
            </div>
            <div className="live-embed reveal">
              <div className="live-embed-bar">
                <span className="live-embed-dot" />
                <span className="live-embed-url">my360.site/p/{DEMO_SHARE_ID}</span>
                <span className="live-embed-tag">{t('landing:live.tag')}</span>
              </div>
              <div className="live-embed-body" id="lpEmbed">
                <iframe
                  className="live-embed-frame"
                  src={`https://my360.site/p/${DEMO_SHARE_ID}`}
                  title={t('landing:live.tag')}
                  loading="lazy"
                  allow="xr-spatial-tracking; gyroscope; accelerometer"
                />
                <button type="button" className="live-embed-shield" id="lpShield">
                  <span className="live-embed-shield-pill">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 3.5v9M9 12.5l-2.2-2a1.6 1.6 0 0 0-2.3 2.2l4.3 5.6A4 4 0 0 0 12 20h2a4 4 0 0 0 4-4v-3.4a1.6 1.6 0 0 0-3.2 0M12 12.6V11a1.6 1.6 0 0 1 3.2 0v1.6M9 3.5a1.6 1.6 0 0 1 3.2 0V11" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    {t('landing:live.shield')}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ── Features ── */}
        <section id="features" className="section" data-chapter="09" data-chapter-label={t('landing:features.eyebrow')}>
          <div className="wrap">
            <div className="section-head center reveal">
              <span className="eyebrow">{t('landing:features.eyebrow')}</span>
              <h2>{t('landing:features.title')}</h2>
            </div>
            <div className="cell-grid cols-3">
              {featureList.map((f, i) => (
                <div className="cell reveal magnet-cell" key={i}>
                  <div className="cell-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" /></svg>
                  </div>
                  <h3>{f.t}</h3>
                  <p>{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Comparison ── */}
        <section id="compare" className="section alt" data-chapter="10" data-chapter-label={t('landing:compare.eyebrow')}>
          <div className="wrap">
            <div className="section-head center reveal">
              <span className="eyebrow">{t('landing:compare.eyebrow')}</span>
              <h2>{t('landing:compare.title')}</h2>
            </div>
            <div className="vs reveal">
              <div className="vs-tabs" role="tablist" aria-label={t('landing:a11y.compareTabs')}>
                {(Object.keys(VS_NAMES) as (keyof typeof VS_NAMES)[]).map((k) => (
                  <button key={k} className={`vs-tab${vsTab === k ? ' on' : ''}`} role="tab"
                    aria-selected={vsTab === k} onClick={() => setVsTab(k as typeof vsTab)}>
                    {VS_NAMES[k]}
                  </button>
                ))}
              </div>
              <div className="vs-table">
                <div className="vs-head"><span /><span className="vs-col-my">my360</span><span className="vs-col-them">{VS_NAMES[vsTab]}</span></div>
                {VS[vsTab].map((row, i) => (
                  <div className="vs-row" key={i}>
                    <span className="vs-feat">{row[0]}</span>
                    <span className="vs-cell yes" data-who="my360">{CHECK}{row[1]}</span>
                    <span className={`vs-cell ${row[2] ? 'yes' : 'no'}`} data-who={VS_NAMES[vsTab]}>{row[2] ? CHECK : DASH}{row[3]}</span>
                  </div>
                ))}
              </div>
              <p className="vs-note">{t('landing:compare.note')}</p>
            </div>
          </div>
        </section>

        {/* ── Pricing ── */}
        <section id="pricing" className="section" data-chapter="11" data-chapter-label={t('landing:pricing.eyebrow')}>
          <div className="wrap">
            <div className="section-head center reveal">
              <span className="eyebrow">{t('landing:pricing.eyebrow')}</span>
              <h2>{t('landing:pricing.title')}</h2>
            </div>
            <div className="price-grid">
              <div className="price reveal">
                <span className="price-name">{t('landing:pricing.free')}</span>
                <div className="price-num">{formatPriceBRL(PLANS.free.priceCents)}</div>
                <p className="price-desc">{t('landing:pricing.freeDesc', planFacts('free'))}</p>
                <a href={appUrl("/register")} className="btn magnet">{t('landing:pricing.freeCta')}</a>
              </div>
              <div className="price hi reveal">
                <span className="price-badge">{t('landing:pricing.proBadge')}</span>
                <span className="price-name">{t('landing:pricing.pro')}</span>
                <div className="price-num">{formatPriceBRL(PLANS.pro.priceCents)}<span>{t('landing:pricing.perMonth')}</span></div>
                <p className="price-desc">{t('landing:pricing.proDesc', planFacts('pro'))}</p>
                <a href={appUrl("/register")} className="btn btn-primary magnet">{t('landing:pricing.proCta')}</a>
              </div>
              <div className="price reveal">
                <span className="price-name">{t('landing:pricing.enterprise')}</span>
                <div className="price-num">{formatPriceBRL(PLANS.enterprise.priceCents)}<span>{t('landing:pricing.perMonth')}</span></div>
                <p className="price-desc">{t('landing:pricing.enterpriseDesc', planFacts('enterprise'))}</p>
                <a href={appUrl("/register")} className="btn magnet">{t('landing:pricing.enterpriseCta')}</a>
              </div>
            </div>
          </div>
        </section>

        {/* ── Downloads ── */}
        <section id="downloads" className="section" data-chapter="12" data-chapter-label={t('landing:downloads.eyebrow')}>
          <div className="wrap">
            <div className="section-head center reveal">
              <span className="eyebrow">{t('landing:downloads.eyebrow')}</span>
              <h2>{t('landing:downloads.title')}</h2>
              <p>{t('landing:downloads.sub')}</p>
            </div>
            <div className="dl-grid">
              <a className="dl-card reveal magnet-cell" href="https://my360.site" target="_blank" rel="noopener noreferrer">
                <span className="dl-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="9.2" /><path d="M2.8 12h18.4M12 2.8c2.6 2.8 2.6 15.6 0 18.4M12 2.8c-2.6 2.8-2.6 15.6 0 18.4" /></svg></span>
                <h3>{t('landing:downloads.web')}</h3>
                <p>{t('landing:downloads.webDesc')}</p>
                <span className="dl-cta">{t('landing:downloads.webCta')}</span>
              </a>
              <a className="dl-card reveal magnet-cell" href={APK_URL}>
                <span className="dl-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 9.5h12v7.2a1.3 1.3 0 0 1-1.3 1.3H7.3A1.3 1.3 0 0 1 6 16.7V9.5zM4.3 9.4c.7 0 1.2.6 1.2 1.3v4.1a1.2 1.2 0 1 1-2.5 0v-4.1c0-.7.6-1.3 1.3-1.3zm15.4 0c.7 0 1.3.6 1.3 1.3v4.1a1.2 1.2 0 1 1-2.5 0v-4.1c0-.7.5-1.3 1.2-1.3zM9 18.6h1.6v2.2a1.1 1.1 0 1 1-2.3 0v-2.2H9zm4.4 0H15v2.2a1.1 1.1 0 1 1-2.3 0v-2.2h.7zM8.6 3.3l.9 1.7A6.5 6.5 0 0 1 12 4.6c.9 0 1.7.1 2.5.4l.9-1.7a.4.4 0 1 1 .7.4l-.9 1.6A5.7 5.7 0 0 1 18 8.6H6a5.7 5.7 0 0 1 2.8-3.3l-.9-1.6a.4.4 0 1 1 .7-.4zM9.4 6.6a.6.6 0 1 0 0 1.2.6.6 0 0 0 0-1.2zm5.2 0a.6.6 0 1 0 0 1.2.6.6 0 0 0 0-1.2z" /></svg></span>
                <h3>{t('landing:downloads.android')}</h3>
                <p>{t('landing:downloads.androidDesc')}</p>
                <span className="dl-cta">{t('landing:downloads.androidCta')}</span>
              </a>
              <div className="dl-card soon reveal">
                <span className="dl-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 5.6l7.2-1v6.9H3V5.6zm8.4-1.2L21 3v8.5h-9.6V4.4zM3 12.7h7.2v6.8L3 18.5v-5.8zm8.4 0H21V21l-9.6-1.3v-7z" /></svg></span>
                <h3>{t('landing:downloads.windows')} <span className="dl-badge">{t('landing:downloads.soon')}</span></h3>
                <p>{t('landing:downloads.windowsDesc')}</p>
                <span className="dl-cta muted">{t('landing:downloads.windowsCta')}</span>
              </div>
              <div className="dl-card soon reveal">
                <span className="dl-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="2" y="7.5" width="20" height="9" rx="3.4" /><path d="M9.6 16.5c.7-1.4 1.5-2.1 2.4-2.1s1.7.7 2.4 2.1" strokeLinecap="round" /></svg></span>
                <h3>{t('landing:downloads.vr')}</h3>
                <p>{t('landing:downloads.vrDesc')}</p>
                <span className="dl-cta muted">{t('landing:downloads.vrCta')}</span>
              </div>
            </div>
            <p className="dl-note">{t('landing:downloads.note')}</p>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section id="faq" className="section alt" data-chapter="13" data-chapter-label={t('landing:faq.eyebrow')}>
          <div className="wrap">
            <div className="section-head center reveal">
              <span className="eyebrow">{t('landing:faq.eyebrow')}</span>
              <h2>{t('landing:faq.title')}</h2>
            </div>
            <div className="faq-list">
              {faqs.map((f, i) => (
                <div className={`faq-item${faqOpen === i ? ' open' : ''}`} key={i}>
                  <button className="faq-q" onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}>
                    <span>{f.q}</span><span className="faq-plus" />
                  </button>
                  <div className="faq-a-wrap"><p className="faq-a">{f.a}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* ── Final CTA ── */}
      <section className="final" id="final" data-chapter="14" data-chapter-label={t('landing:final.eyebrow')}>
        <div className="wrap final-inner reveal">
          <span className="eyebrow">{t('landing:final.eyebrow')}</span>
          <h2>{t('landing:final.title')}</h2>
          <p>{t('landing:final.sub')}</p>
          <div className="hero-ctas" style={{ justifyContent: 'center' }}>
            <a href={appUrl("/register")} className="btn btn-primary lg magnet">{t('landing:final.ctaPrimary')}</a>
            <a href="#pricing" className="btn lg magnet">{t('landing:final.ctaSecondary')}</a>
          </div>
        </div>
      </section>

      <footer id="lpFooter">
        <div className="wrap">
          <div className="foot-top">
            <div className="foot-brand">
              <a href="#top" className="brand">
                <img className="brand-logo" src="/logo.png" alt="" width={30} height={30} />
                <span className="brand-name">my360</span>
              </a>
              <p className="foot-pitch">{t('landing:footer.pitch')}</p>
              <div className="foot-platforms">
                <a href="https://my360.site" target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" /></svg>
                  {t('landing:downloads.web')}
                </a>
                <a href={APK_URL}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="6" y="2" width="12" height="20" rx="2" /><path d="M10 18h4" strokeLinecap="round" /></svg>
                  {t('landing:downloads.android')}
                </a>
                <span className="soon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="4" width="18" height="13" rx="1" /><path d="M8 21h8M12 17v4" strokeLinecap="round" /></svg>
                  {t('landing:downloads.windows')}
                </span>
              </div>
            </div>

            <nav className="foot-nav">
              <div className="foot-col">
                <h4>{t('landing:footer.product')}</h4>
                <a href="#how">{t('landing:nav.how')}</a>
                <a href="#editor">{t('landing:nav.editor')}</a>
                <a href="#ai">{t('landing:footer.aiStaging')}</a>
                <a href="#vr">{t('landing:footer.vrTours')}</a>
                <a href="#tours">{t('landing:footer.demoTour')}</a>
              </div>
              <div className="foot-col">
                <h4>{t('landing:footer.buy')}</h4>
                <a href="#pricing">{t('landing:footer.plans')}</a>
                <a href="#compare">{t('landing:footer.comparePlatforms')}</a>
                <a href="#faq">{t('landing:footer.faq')}</a>
              </div>
              {/* Coluna de SEGMENTOS: é por aqui que o Google descobre as
                  páginas de solução e distribui autoridade da home pra
                  elas. Link em toda a landing, não só num lugar. */}
              <div className="foot-col">
                <h4>{t('solutions:breadcrumbSolutions')}</h4>
                {SOLUTION_SLUGS.map((sl) => (
                  <Link key={sl} to="/solucoes/$segment" params={{ segment: sl }}>
                    {t(SOLUTION_NAME_KEY[sl])}
                  </Link>
                ))}
              </div>
              <div className="foot-col">
                <h4>{t('landing:footer.account')}</h4>
                <a href={appUrl("/login")}>{t('landing:footer.signIn')}</a>
                <a href={appUrl("/register")}>{t('landing:footer.createAccount')}</a>
                <Link to="/status">{t('landing:footer.status')}</Link>
              </div>
            </nav>
          </div>

          <div className="foot-bottom">
            <span>{t('landing:footer.copyright')}</span>
            <div className="foot-legal">
              <Link to="/privacy">{t('landing:footer.privacy')}</Link>
              <Link to="/terms">{t('landing:footer.terms')}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
